import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { PencilSquareIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface PromoCodeRow {
  _id: string;
  codeDisplay: string;
  codeNormalized: string;
  type: 'percentage' | 'fixed';
  value: number;
  maxDiscount?: number;
  minAmount: number;
  usageLimit?: number;
  usagePerUser: number;
  validFrom: string;
  validTo: string;
  applicableOn: 'orders' | 'chef' | 'both';
  applicableChefIds?: string[];
  applicableRestaurantIds?: string[];
  isActive: boolean;
  usedCount: number;
}

interface ChefOption {
  _id: string;
  name?: string;
  email?: string;
}

type PromoDiscountType = 'percentage' | 'fixed';
type PromoApplicableOn = 'orders' | 'chef' | 'both';

interface PromoFormState {
  code: string;
  type: PromoDiscountType;
  value: number;
  maxDiscount: string | number;
  minAmount: number;
  usageLimit: string | number;
  usagePerUser: number;
  validFrom: string;
  validTo: string;
  applicableOn: PromoApplicableOn;
  chefIdSet: Set<string>;
  restaurantIdsText: string;
  isActive: boolean;
}

function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function emptyForm(): PromoFormState {
  return {
    code: '',
    type: 'percentage',
    value: 10,
    maxDiscount: '',
    minAmount: 0,
    usageLimit: '',
    usagePerUser: 1,
    validFrom: toLocalInput(new Date().toISOString()),
    validTo: toLocalInput(new Date(Date.now() + 30 * 864e5).toISOString()),
    applicableOn: 'both',
    chefIdSet: new Set<string>(),
    restaurantIdsText: '',
    isActive: true,
  };
}

export default function PromoCodesTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [rows, setRows] = useState<PromoCodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listFilter, setListFilter] = useState<'all' | 'active' | 'expired'>('all');
  const [chefs, setChefs] = useState<ChefOption[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<PromoFormState>(() => emptyForm());

  const applicableNeedsChef = form.applicableOn === 'chef' || form.applicableOn === 'both';
  const applicableNeedsRestaurant = form.applicableOn === 'orders' || form.applicableOn === 'both';

  const loadList = async () => {
    try {
      setLoading(true);
      const params =
        listFilter === 'active'
          ? { active: 'true' }
          : listFilter === 'expired'
            ? { expired: 'true' }
            : {};
      const res = await api.get('/admin/promo-codes', { params });
      const list = (res.data.data || []) as PromoCodeRow[];
      setRows(
        list.map((r) => ({
          ...r,
          applicableChefIds: (r.applicableChefIds || []).map((x: any) => String(x)),
          applicableRestaurantIds: (r.applicableRestaurantIds || []).map((x: any) => String(x)),
        })),
      );
    } catch (e) {
      console.error(e);
      alert(t('promoCodes.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadChefs = async () => {
    try {
      const res = await api.get('/admin/users', { params: { role: 'chef', limit: 500, page: 1 } });
      setChefs(res.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadList();
  }, [listFilter]);

  useEffect(() => {
    loadChefs();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (r: PromoCodeRow) => {
    setEditingId(r._id);
    const chefSet = new Set((r.applicableChefIds || []).map(String));
    setForm({
      code: r.codeDisplay,
      type: r.type,
      value: r.value,
      maxDiscount: r.maxDiscount != null ? r.maxDiscount : '',
      minAmount: r.minAmount ?? 0,
      usageLimit: r.usageLimit != null ? r.usageLimit : '',
      usagePerUser: r.usagePerUser ?? 1,
      validFrom: toLocalInput(r.validFrom),
      validTo: toLocalInput(r.validTo),
      applicableOn: r.applicableOn,
      chefIdSet: chefSet,
      restaurantIdsText: (r.applicableRestaurantIds || []).join(', '),
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const parseOptionalInt = (v: string | number): number | undefined => {
    if (v === '' || v == null) return undefined;
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    return Number.isFinite(n) ? n : undefined;
  };

  const parseOptionalFloat = (v: string | number): number | undefined => {
    if (v === '' || v == null) return undefined;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : undefined;
  };

  const buildPayload = () => {
    const restaurantIds = form.restaurantIdsText
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const maxDiscount = parseOptionalFloat(form.maxDiscount);
    const usageLimit = parseOptionalInt(form.usageLimit);
    return {
      code: form.code.trim(),
      type: form.type,
      value: Number(form.value),
      ...(maxDiscount != null ? { maxDiscount } : {}),
      minAmount: Number(form.minAmount) || 0,
      ...(usageLimit != null ? { usageLimit } : {}),
      usagePerUser: Math.max(1, parseInt(String(form.usagePerUser), 10) || 1),
      validFrom: new Date(form.validFrom).toISOString(),
      validTo: new Date(form.validTo).toISOString(),
      applicableOn: form.applicableOn,
      applicableChefIds: applicableNeedsChef ? Array.from(form.chefIdSet) : [],
      applicableRestaurantIds: applicableNeedsRestaurant ? restaurantIds : [],
      isActive: form.isActive,
    };
  };

  const handleSave = async () => {
    if (!form.code.trim()) {
      alert(t('promoCodes.codeRequired'));
      return;
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      if (editingId) {
        await api.put(`/admin/promo-codes/${editingId}`, payload);
      } else {
        await api.post('/admin/promo-codes', payload);
      }
      setModalOpen(false);
      loadList();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || t('promoCodes.failedToSave');
      alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('promoCodes.confirmDelete'))) return;
    try {
      await api.delete(`/admin/promo-codes/${id}`);
      loadList();
    } catch (err: any) {
      alert(err.response?.data?.message || t('promoCodes.failedToDelete'));
    }
  };

  const toggleChef = (id: string) => {
    setForm((f) => {
      const next = new Set(f.chefIdSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...f, chefIdSet: next };
    });
  };

  const chefLabel = useMemo(() => {
    const m = new Map(chefs.map((c) => [c._id, c.name || c.email || c._id]));
    return (id: string) => m.get(id) || id;
  }, [chefs]);

  if (loading && rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className={`flex flex-wrap justify-between items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
        <h1 className="text-2xl font-bold text-gray-900">{t('promoCodes.title')}</h1>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          {t('promoCodes.add')}
        </button>
      </div>

      <p className="text-sm text-gray-600 max-w-3xl">{t('promoCodes.intro')}</p>

      <div className={`flex flex-wrap gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
        {(['all', 'active', 'expired'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setListFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              listFilter === f ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t(`promoCodes.filter.${f}`)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('promoCodes.code')}</th>
                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('promoCodes.discount')}</th>
                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('promoCodes.appliesTo')}</th>
                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('promoCodes.valid')}</th>
                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('promoCodes.usage')}</th>
                <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 uppercase">{t('promoCodes.status')}</th>
                <th className="px-4 py-3 text-end text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    {t('promoCodes.empty')}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.codeDisplay}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.type === 'percentage' ? `${r.value}%` : `${r.value} EGP`}
                      {r.maxDiscount != null && r.type === 'percentage' && (
                        <span className="text-gray-500"> ({t('promoCodes.max')} {r.maxDiscount})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-orange-50 text-orange-800">
                        {t(`promoCodes.applicableOn.${r.applicableOn}`)}
                      </span>
                      {(r.applicableChefIds?.length ?? 0) > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {r.applicableChefIds!.length} {t('promoCodes.chefsRestricted')}
                        </div>
                      )}
                      {(r.applicableRestaurantIds?.length ?? 0) > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {r.applicableRestaurantIds!.length} {t('promoCodes.restaurantsRestricted')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {new Date(r.validTo).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.usedCount}
                      {r.usageLimit != null ? ` / ${r.usageLimit}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          r.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {r.isActive ? t('promoCodes.active') : t('promoCodes.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`flex justify-end gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="p-2 text-gray-500 hover:text-primary-orange hover:bg-orange-50 rounded-lg"
                          title={t('common.edit')}
                        >
                          <PencilSquareIcon className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(r._id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title={t('common.delete')}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <div className={`sticky top-0 flex justify-between items-center px-6 py-4 border-b bg-white ${isRtl ? 'flex-row-reverse' : ''}`}>
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? t('promoCodes.editTitle') : t('promoCodes.createTitle')}
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label={t('common.cancel')}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.code')} *</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.type')}</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as PromoDiscountType }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="percentage">{t('promoCodes.percentage')}</option>
                    <option value="fixed">{t('promoCodes.fixedAmount')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.value')}</label>
                  <input
                    type="number"
                    min={0}
                    step={form.type === 'percentage' ? 1 : 0.01}
                    value={form.value}
                    onChange={(e) => setForm((f) => ({ ...f, value: parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {form.type === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.maxDiscount')}</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.maxDiscount}
                    onChange={(e) => setForm((f) => ({ ...f, maxDiscount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder={t('common.optional')}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.minAmount')}</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.minAmount}
                    onChange={(e) => setForm((f) => ({ ...f, minAmount: parseFloat(e.target.value) || 0 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.usagePerUser')}</label>
                  <input
                    type="number"
                    min={1}
                    value={form.usagePerUser}
                    onChange={(e) => setForm((f) => ({ ...f, usagePerUser: parseInt(e.target.value, 10) || 1 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.usageLimit')}</label>
                <input
                  type="number"
                  min={1}
                  value={form.usageLimit}
                  onChange={(e) => setForm((f) => ({ ...f, usageLimit: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder={t('promoCodes.unlimitedPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.validFrom')}</label>
                  <input
                    type="datetime-local"
                    value={form.validFrom}
                    onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.validTo')}</label>
                  <input
                    type="datetime-local"
                    value={form.validTo}
                    onChange={(e) => setForm((f) => ({ ...f, validTo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.appliesTo')}</label>
                <select
                  value={form.applicableOn}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, applicableOn: e.target.value as PromoApplicableOn }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="orders">{t('promoCodes.applicableOn.orders')}</option>
                  <option value="chef">{t('promoCodes.applicableOn.chef')}</option>
                  <option value="both">{t('promoCodes.applicableOn.both')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">{t('promoCodes.applicableHint')}</p>
              </div>

              {applicableNeedsChef && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('promoCodes.chefScope')}</label>
                  <p className="text-xs text-gray-500 mb-2">{t('promoCodes.chefScopeHint')}</p>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {chefs.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('promoCodes.noChefsLoaded')}</p>
                    ) : (
                      chefs.map((c) => (
                        <label key={c._id} className={`flex items-center gap-2 text-sm ${isRtl ? 'flex-row-reverse' : ''}`}>
                          <input
                            type="checkbox"
                            checked={form.chefIdSet.has(c._id)}
                            onChange={() => toggleChef(c._id)}
                          />
                          <span>{chefLabel(c._id)}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {applicableNeedsRestaurant && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoCodes.restaurantIds')}</label>
                  <textarea
                    value={form.restaurantIdsText}
                    onChange={(e) => setForm((f) => ({ ...f, restaurantIdsText: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    rows={2}
                    placeholder={t('promoCodes.restaurantIdsPlaceholder')}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('promoCodes.restaurantIdsHint')}</p>
                </div>
              )}

              <label className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                <span className="text-sm text-gray-700">{t('promoCodes.isActive')}</span>
              </label>
            </div>

            <div className={`sticky bottom-0 flex gap-3 justify-end px-6 py-4 border-t bg-gray-50 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-primary-orange text-white hover:bg-orange-600 disabled:opacity-50"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
